import { Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { EnvironmentService } from './environment.service';
import { LocalizationService } from './localization.service';
import { EFFilterData, EFItinerary, EFItineraryContent, EFItineraryContentResource, EFPOI, EFProduct, EFProductType, EFSupplier } from '../models/experience.model';
import { PGUtilities } from '../utilities/pg-utilities';
import { TimetableDataListItem } from '../models/timetable.model';
import { DataFilter } from './data.service';

class EFGeoJSON {
    type: "FeatureCollection"
    name: string
    features: Array<{ 
        type: "Feature", 
        properties: any, 
        geometry: { 
            type: "Point", 
            coordinates: [ number, number ] 
        } 
    }>
}

export class EFFinderEnvironment {
    pid:string = null
    realm_id:string = null

    data:{
        color:string,
        logo:string,
        name:string,
        title:string,
        view:'map'|'list'|'calendar',
        back:string,
        backOnLogo:boolean,
        footerLogo:string,
        footerLarge:boolean,
        operatorsBanner:boolean,
        showcase:boolean,
        showcaseWithQR:string,
        showcaseContent:Array<{
            type:string,
            element:string
        }>,
        filter:{
            type:string,
            category:string
            tags:string,
            users:string
        } 
    } = null;

    constructor(fromData?:any) {
        if(fromData != null) {
            for(let i in fromData) {
                if(typeof this[i] != 'undefined') {
                    if(i == 'data') {
                        this[i] = PGUtilities.tryParseJSON(fromData[i])
                        this[i].showcaseContent = PGUtilities.tryParseJSON(this[i].showcaseContent)
                        this[i].filter = PGUtilities.tryParseJSON(this[i].filter)
                    }
                    else {
                        this[i] = fromData[i]
                    }
                }
            }
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class EFDataService {
    constructor(private http:HttpClient, private environmentService:EnvironmentService, private localizationService:LocalizationService) {
        for(let _filter of ['Event','Package']) {
            this.localizationService.addTranslationLink('OPTIONMAPS.' + _filter, 'OPTIONMAPS.Experience')
            this.localizationService.addTranslationLink('OPTIONMAPS.Experience~' + _filter, 'OPTIONMAPS.Experience')
            this.localizationService.addTranslationLink('RESOURCES.Experience~' + _filter + '.fields', 'RESOURCES.Experience.fields')
            this.localizationService.addIconLink('OPTIONMAPS.' + _filter, 'OPTIONMAPS.Experience')
            this.localizationService.addIconLink('OPTIONMAPS.Experience~' + _filter, 'OPTIONMAPS.Experience')
        }
    }

    listSuppliers(language:string, realm?:string) {
        return new Observable<Array<EFSupplier>>((observer) => {
            let _retVal:Array<EFSupplier> = [];

            let _reqResources = ['host','eatery','experienceSupplier'];
            let _reqNum = 0;

            for(let _resource of _reqResources) {
                _reqNum++
                this._getResourceWithCache(_resource, language, null, null, realm).subscribe(data => {
                    for(let _cData of data as Array<any>) {
                        _cData.id = _resource.charAt(0).toUpperCase() + _resource.substring(1) + '_' + _cData.id

                        _retVal.push(new EFSupplier(_cData, _resource))
                    }
    
                    observer.next(_retVal);
                }, (error) => {
                    observer.next(_retVal);
                }, () => {
                    _reqNum--
                    if(_reqNum <= 0) {
                        observer.complete()
                        observer.unsubscribe()
                    }
                })
            }
        })
    }

    getSupplier(id:string) {
        return new Observable<EFSupplier>((observer) => {
            let _resource = id.split('_')[0];
            let _element = id.split('_')[1];

            this.http.get(this.environmentService.environment.APIUrl + '/public/' + this.normalizeResourceName(_resource) + '/' + _element).subscribe((data:any) => {
                observer.next(new EFSupplier(data, _resource))
                observer.unsubscribe()
            }, () => {
                observer.next(null)
                observer.unsubscribe()
            })
        })
    }

    listPOIs(language:string, realm?:string) {
        return new Observable<Array<EFPOI>>((observer) => {
            let _retVal:Array<EFPOI> = [];

            this._getResourceWithCache('Poi', language, null, null, realm).subscribe(data => {
                for(let _cData of data as Array<any>) {
                    _retVal.push(new EFPOI(_cData))
                }

                observer.next(_retVal);
            }, () => {
                observer.next(_retVal);
            }, () => {
                observer.complete();
                observer.unsubscribe();
            })
        })
    }

    getPOI(id:string) {
        return new Observable<EFPOI>((observer) => {
            this.http.get(this.environmentService.environment.APIUrl + '/public/poi/' +  id).subscribe((data:any) => {
                observer.next(new EFPOI(data))
                observer.unsubscribe()
            }, () => {
                observer.next(null)
                observer.unsubscribe()
            })
        })
    }

    private _getDatesFromAvailability(availability:string, withDays?:boolean) {
        if(availability != null) {
            let _availabilityList:Array<TimetableDataListItem> = PGUtilities.tryParseJSON(availability);

            let _dates:Array<string|{ begin: string, end: string, days:string }> = [];

            let _lapseList:Array<{ begin: Date, end: Date, days:string }> = [];

            for(let _availability of _availabilityList) {
                if(_availability.periods != null) {
                    let _dayString:string = null;

                    if(withDays) {
                        let _daysList = [false,false,false,false,false,false,false];

                        if(_availability.hours != null) {
                            for(let _hour of _availability.hours) {
                                for(let i = 0; i < 7; i++) {
                                    if(_hour.days[i]) _daysList[i] = true;
                                }
                            }
                        }

                        _dayString = '';
                        for(let _item of _daysList) {
                            _dayString += _item ? '1' : '0'
                        }
                    }
                    
                    for(let _days of _availability.periods) {
                        if(_days.begin != null) {
                            if(_dates == null) _dates = [];
                            let _cYear = _days.year;
                            if(_cYear == null) _cYear = new Date().getFullYear();

                            let _beginDate = new Date(_cYear, _days.begin.month - 1, _days.begin.day);

                            if(_days.end == null) {
                                if(_days.year == null) { // se è un evento ciclico annuale, imposto l'anno al prossimo inizio futuro
                                    if(_beginDate.getTime() < Date.now()) {
                                        _beginDate.setFullYear(_beginDate.getFullYear() + 1)
                                    }
                                }

                                _lapseList.push({ begin: _beginDate, end: _beginDate, days: _dayString })
                            }
                            else {
                                let _endDate = new Date(_beginDate.getFullYear(), _days.end.month - 1, _days.end.day);
                                if(_endDate.getTime() < _beginDate.getTime()) _endDate.setFullYear(_endDate.getFullYear() + 1) // se la data di fine è precedente a quella di inizio la sposto all'anno dopo

                                if(_days.year == null) { // se è un evento ciclico annuale, imposto l'anno alla prossima fine futura
                                    if(_endDate.getTime() < Date.now()) {
                                        _beginDate.setFullYear(_beginDate.getFullYear() + 1)
                                        _endDate.setFullYear(_endDate.getFullYear() + 1)
                                    }
                                }

                                // setto la fine al giorno +1, in modo che si riesca a fare il merge per inizio = fine
                                _endDate.setDate(_endDate.getDate() + 1)

                                // NB: rimosso filtro per date vecchie
                                //if(_endDate.getTime() > Date.now()) {
                                    _lapseList.push({ begin: _beginDate, end: _endDate, days: _dayString })
                                //}
                            }
                        }
                    }
                }
            }

            let _mergedList:Array<{ begin: Date, end: Date, days:string }> = []

            for(let _item of _lapseList) {
                let _wasMerged = false;

                for(let _mergedItem of _mergedList) {
                    if(_item.days == _mergedItem.days && _item.begin.getTime() <= _mergedItem.end.getTime() && _item.end.getTime() >= _mergedItem.begin.getTime()) {

                        if(_item.begin.getTime() < _mergedItem.begin.getTime()) {
                            _mergedItem.begin = _item.begin;
                        }

                        if(_item.end.getTime() > _mergedItem.end.getTime()) {
                            _mergedItem.end = _item.end;
                        }

                        _wasMerged = true;
                        break;
                    }
                }

                if(!_wasMerged) _mergedList.push(_item)
            }

            for(let _item of _mergedList) {
                _item.end.setDate(_item.end.getDate() - 1)  // tolgo il giorno che avevo aggiunto prima

                let _beginString = this._getDateString(_item.begin.getFullYear(), _item.begin.getMonth() + 1, _item.begin.getDate())
                let _endString = this._getDateString(_item.end.getFullYear(), _item.end.getMonth() + 1, _item.end.getDate())

                if(_endString == _beginString) {
                    _dates.push(_beginString)
                }
                else {
                    _dates.push({ begin: _beginString, end: _endString, days: _item.days })
                }
            }

            return _dates;
        }
    }

    private _getDateString(year:number, month:number, day:number) {
        let _retVal = year + '-'
        if(month  < 10) _retVal += '0';
        _retVal += month + '-'
        if(day  < 10) _retVal += '0';
        _retVal += day

        return _retVal;
    }

    private _mapProductData: { [resource:string]: (data:any) => any } = {
        experience: (data) => {

            if(typeof data.payment == 'string') {
                data.payment = [data.payment]
            }

            if(data.availability != null) {
                data.dates = this._getDatesFromAvailability(data.availability)
                data.dates_days = this._getDatesFromAvailability(data.availability, true)
            }

            return data;
        },
        'experience~Event': (data) => {
            return this._mapProductData.experience(data);
        },
        'experience~Package': (data) => {
            return this._mapProductData.experience(data);
        },
        host: (data) => {
            data.title = data.name;
            data.category = data.type;

            if(data.translations != null) {
                for(let _cTranslation of data.translations) {
                    _cTranslation.title = _cTranslation.name
                }
            }

            return data;
        },
        eatery: (data) => {
            data.title = data.name;
            data.category = data.type;

            if(data.translations != null) {
                for(let _cTranslation of data.translations) {
                    _cTranslation.title = _cTranslation.name
                }
            }

            return data;
        },
        experienceSupplier: (data) => {
            //data.title = data.name;
            //TODO: va ripristinato il mapping su name
            data.title = data.company_name;

            this.localizationService.addTranslationAlias('OPTIONMAPS.EFService.category.' + data.category, 'OPTIONMAPS.ExperienceSupplier.category.' + data.category)
            this.localizationService.addIconAlias('OPTIONMAPS.EFService.category.' + data.category, 'OPTIONMAPS.ExperienceSupplier.category.' + data.category)

            if(data.translations != null) {
                for(let _cTranslation of data.translations) {
                    _cTranslation.title = _cTranslation.name
                }
            }

            return data;
        },
        good: (data) => {
            data.title = data.name;

            if(data.supplier_type != null && data.supplier_id != null) {
                data.supplier = data.supplier_type + '_' + data.supplier_id;
            }

            if(data.translations != null) {
                for(let _cTranslation of data.translations) {
                    _cTranslation.title = _cTranslation.name
                }
            }

            return data;
        },
        poi: (data) => {
            data.title = data.name;

            this.localizationService.addTranslationAlias('OPTIONMAPS.EFPOI.category.' + data.category, 'OPTIONMAPS.Poi.category.' + data.category)
            this.localizationService.addIconAlias('OPTIONMAPS.EFPOI.category.' + data.category, 'OPTIONMAPS.Poi.category.' + data.category)
            
            this.localizationService.addTranslationAlias('OPTIONMAPS.EFPOI.subcategory.' + data.subcategory, 'OPTIONMAPS.Poi.subcategory.' + data.subcategory)

            if(data.availability != null) {
                data.dates = this._getDatesFromAvailability(data.availability)
                data.dates_days = this._getDatesFromAvailability(data.availability, true)
            }

            if(data.translations != null) {
                for(let _cTranslation of data.translations) {
                    _cTranslation.title = _cTranslation.name
                }
            }

            return data;
        },
        itinerary: (data) => {
            // TODO: sarà da sostituire in qualche modo?
            delete data.services

            data.title = data.name;

            if(data.duration != null && data.duration != '') {
                data.duration = data.duration * 60 * 60 * 1000
            }
            
            if(data.travel_time != null && data.travel_time != '') {
                data.travel_time = data.travel_time * 1000
            }

            if(data.travel_distance != null && data.travel_distance != '') {
                data.travel_distance = data.travel_distance
            }

            data.travel_stops = data.collection?.length;
             
            if(data.translations != null) {
                for(let _cTranslation of data.translations) {
                    _cTranslation.title = _cTranslation.name
                }
            }
            
            return data;
        },
        article: (data) => {
            data.description = data.text;

            if(data.translations != null) {
                for(let _cTranslation of data.translations) {
                    _cTranslation.description = _cTranslation.text
                }
            }

            return data;
        },
        'musement:experience': (data) => {
            if(data.category != null && typeof data.category == 'object') {
                this.localizationService.addTranslation('*', 'OPTIONMAPS.Experience.category.' + data.category.code, data.category.name)
                if(data.category.translations != null) {
                    for(let _translation of data.category.translations) {
                        this.localizationService.addTranslation(_translation.language, 'OPTIONMAPS.Experience.category.' + data.category.code, _translation.name)
                    }
                }
                data.category = data.category.code;
            }
     
            data = this._mapProductData.experience(data);

            return data;
        }
    }

    private _checkMapProductData(data:any, resource:string) {
        if(this._mapProductData[resource] != null) {
            return this._mapProductData[resource](data);
        }
        else return data;
    }

    private _mapProductType: { [resource:string]: EFProductType } = {
        'experience~Event': 'event',
        'experience~Package': 'package',
        'experienceSupplier': 'service',
        'musement:experience': 'experience'
    }

    private _checkMapProductType(resource:string):EFProductType {
        resource = this.normalizeResourceName(resource)

        if(this._mapProductType[resource] != null) {
            return this._mapProductType[resource];
        }
        else return resource as any;
    }

    getProductTypeFromResource(resource:string):EFProductType {
        return this._checkMapProductType(resource)
    }

    getResourceFromProductType(type:string, onlyInternal?:boolean):string {
        for(let i in this._mapProductType) {
            if(!/:/.test(i)) {
                if(this._mapProductType[i] == type) return i
            }
        }

        return type;
    }

    private _resourceFilters:{ 
        [resource:string]: {
            [filter:string]: Array<any>
        }
    } = {
        'experience': {
            'Event': [{
                field: 'type', operator: '==', value: ['event']
            }],
            'Package': [{
                field: 'type', operator: '==', value: ['package']
            }],
            '': [{
                field: 'type', operator: '==', value: ['activity']
            }]
        }
    }

    private _resourcesCache:{ [resource:string] : { [language:string]: { [filter:string] : Array<any> } } } = {}

    resourcesCacheReset() {
        this._resourcesCache = {};
    }

    private _getResourcesCache(resource:string, language:string, filter:string) {
        if(this._resourcesCache[resource] != null && this._resourcesCache[resource][language] != null && this._resourcesCache[resource][language][filter] != null) return JSON.parse(JSON.stringify(this._resourcesCache[resource][language][filter]));
    }

    private _setResourcesCache(resource:string, language:string, filter:string, list:Array<any>) {
        if(this._resourcesCache[resource] == null) this._resourcesCache[resource] = {}
        if(this._resourcesCache[resource][language] == null) this._resourcesCache[resource][language] = {}

        this._resourcesCache[resource][language][filter] = list

        return this._getResourcesCache(resource, language, filter)
    }

    private _getResourceWithCache(resource:string, language:string, groups?:Array<string>, parent?:string, realm?:string) {
        return new Observable<Array<any>>((observer) => {
            resource = this.normalizeResourceName(resource);

            let _realm = realm || this.environmentService.publicRealm?.id;

            let _filterString = '';

            if(_realm != null) {
                _filterString += _realm;
            }

            if(groups != null) {
                let _groups = JSON.parse(JSON.stringify(groups))
                _groups.sort();
                _filterString += JSON.stringify(_groups);
            }
            else {
                _filterString += '[]'
            }
            
            if(parent != null) {
                _filterString += parent;
            }

            let _cache = this._getResourcesCache(resource, language, _filterString)

            if(_cache != null) {
                setTimeout(() => {
                    observer.next(JSON.parse(JSON.stringify(_cache)))
                    observer.complete();
                    observer.unsubscribe();
                }, 100)
            }
            else {
                let _resourceId = resource.split('~')[0];
                let _filterId = resource.split('~')[1];
                if(_filterId == null) _filterId = '';
                let _filterList:Array<DataFilter> = []

                let _useV1 = false;

                if(this._resourceFilters[_resourceId] != null && this._resourceFilters[_resourceId][_filterId] != null) {
                    _filterList = JSON.parse(JSON.stringify(this._resourceFilters[_resourceId][_filterId]))
                }

                if(groups != null) {
                    _filterList.push({
                        field: 'group_id', operator: 'in', value: groups
                    })
                }
                
                if(parent != null) {
                    _filterList.push({
                        field: 'parent', operator: '==', value: [parent.split('_')[1]]
                    })

                    _useV1 = true;
                }

                let _filterQuery = '';

                if(_filterList.length > 0) {
                    _filterQuery = '&filter=' + encodeURIComponent(JSON.stringify(_filterList))
                }

                if(_realm != null) {
                    _filterQuery += '&realm=' + _realm;
                }

                this._getResourcePaged(_resourceId, _filterQuery, language, _useV1).subscribe((data) => {
                    observer.next(this._setResourcesCache(resource, language, _filterString, data))
                }, () => {
                    observer.next(this._setResourcesCache(resource, language, _filterString, []))
                }, () => {
                    observer.complete();
                    observer.unsubscribe();
                })
            }
        })
    }

    private _getResourcePaged(resource:string, query:string, language:string, useV1?:boolean) {
        return new Observable<Array<any>>((observer) => {
            this._getResourcePagedRec(resource, query, language, observer, [], 0, useV1)
        })
    }

    private _getResourcePagedRec(resource:string, query:string, language:string, observer:Subscriber<Array<any>>, append:Array<any>, page:number, useV1?:boolean) {
        let _pageSize = 250;

        if(useV1) {
            this.http.get(this.environmentService.environment.APIUrl + '/public/' + resource + '?offset=' + (_pageSize * page) + '&limit=' + (_pageSize + 1) + query).subscribe((data:Array<any>) => {
                for(let i = 0; i < _pageSize; i++) {
                    if(data[i] != null) {
                        append.push(data[i]);
                    }
                }

                observer.next(append);

                if(data.length <= _pageSize) {
                    observer.complete();
                    observer.unsubscribe();
                }
                else {
                    this._getResourcePagedRec(resource, query, language, observer, append, page + 1, useV1)
                }
            }, (error) => {
                observer.next(append);
                observer.complete();
                observer.unsubscribe();
            })
        }
        else {
            // V2
    
            let _filter = ''
    
            if(query) {
                let _querySplit = query.split('&');
                for(let _item of _querySplit) {
                    let _itemSplit = _item.split('=')
                    if(_itemSplit[0] == 'realm') {
                        _filter += '&realm_id=' + _itemSplit[1];
                    }
                    else if(_itemSplit[0] == 'filter') {
                        let _filterList:Array<DataFilter> = PGUtilities.tryParseJSON(decodeURIComponent(_itemSplit[1]))
    
                        if(_filterList) {
                            for(let _item of _filterList) {
                                if(_item.operator == '==') {
                                    _filter += '&' + _item.field + '=' + encodeURIComponent(_item.value[0]);
                                }
                                else if(_item.operator == 'in') {
                                    _filter += '&' + _item.field + '=' + encodeURIComponent(JSON.stringify(_item.value));
                                }
                            }
                        }
                    }
                }
            }
    
            this.http.get(this.environmentService.environment.APIUrl + '/v2/public/' + resource + '?lang=' + language + '&page=' + (page + 1) + '&per_page=' + _pageSize + _filter).subscribe((data:{ data: Array<any>, meta: { total: number } }) => {
                for(let i = 0; i < _pageSize; i++) {
                    if(data.data[i] != null) {
                        append.push(data.data[i]);
                    }
                }

                observer.next(append);

                if(data.meta.total <= _pageSize * (page + 1)) {
                    observer.complete();
                    observer.unsubscribe();
                }
                else {
                    this._getResourcePagedRec(resource, query, language, observer, append, page + 1, useV1)
                }
            }, (error) => {
                observer.next(append);
                observer.complete();
                observer.unsubscribe();
            })
        }
    }

    normalizeResourceName(resource:string) {
        return resource.charAt(0).toLowerCase() + resource.substring(1);
    }

    private _getResourceProductAnalytics(resource:string, product:any) {
        let _analyticsResource = resource.replace(/~.*$/, '');
        let _analyticsType = null;
        if(_analyticsResource == 'experience') _analyticsType = product.type;

        return { id: product.id, resource: _analyticsResource, type: _analyticsType };
    }

    listResourceProducts(resource:string, language:string, groups?:Array<string>, parent?:string, realm?:string) {
        let _type = this._checkMapProductType(resource);

        return this._getResourceWithCache(resource, language, groups, parent, realm).pipe(map((data) => {
            let _products:Array<EFProduct> = [];

            for(let _item of data as Array<any>) {
                if(groups == null || groups.length == 0 || groups.indexOf(_item.group_id) != -1) {
                    _products.push(new EFProduct(this.environmentService, this.localizationService, _type, this._checkMapProductData(_item, resource), resource, this._getResourceProductAnalytics(resource, _item)))
                }
            }

            return _products;
        }))
    }

    listSupplierGoods(supplierResource:string, supplierId:string|number, language?:string, realm?:string) {
        return new Observable<Array<EFProduct>>((observer) => {
            if(language == null) language = this.localizationService.currentLanguage;

            supplierResource = this.normalizeResourceName(supplierResource);
            let _supplierType = supplierResource.charAt(0).toUpperCase() + supplierResource.substring(1);

            let _realm = realm || this.environmentService.publicRealm?.id;
            let _filterList:Array<DataFilter> = [
                { field: 'supplier_type', operator: '==', value: [_supplierType] },
                { field: 'supplier_id', operator: '==', value: [supplierId] }
            ];

            let _filterQuery = '&filter=' + encodeURIComponent(JSON.stringify(_filterList));
            if(_realm != null) {
                _filterQuery += '&realm=' + _realm;
            }

            this._getResourcePaged('good', _filterQuery, language, true).subscribe((data) => {
                let _products:Array<EFProduct> = [];

                for(let _item of data as Array<any>) {
                    _products.push(new EFProduct(this.environmentService, this.localizationService, 'good', this._checkMapProductData(_item, 'good'), 'good', this._getResourceProductAnalytics('good', _item)))
                }

                observer.next(_products);
            }, () => {
                observer.next([]);
            }, () => {
                observer.complete();
                observer.unsubscribe();
            })
        })
    }

    isSupplierProductResource(resource:string) {
        resource = this.normalizeResourceName(resource);
        return resource == 'host' || resource == 'eatery' || resource == 'experienceSupplier';
    }

    getResourceProduct(resource:string, element:string, useDataPath?:boolean) {
        return new Observable<EFProduct>((observer) => {
            resource = this.normalizeResourceName(resource);

            let _resourceId = resource.split('~')[0];

            let _modePart = useDataPath ? 'data' : 'public';

            if(!/^\d+$/.test(element)) {
                _modePart += '/slug'
            }

            this.http.get(this.environmentService.environment.APIUrl + '/' + _modePart + '/' + _resourceId + '/' + element, {
                headers: new HttpHeaders({ 'X-Interceptor-Silent': '*' })
            }).subscribe((data) => {
                observer.next(new EFProduct(this.environmentService, this.localizationService, this._checkMapProductType(resource), this._checkMapProductData(data, resource), resource, this._getResourceProductAnalytics(resource, data)))
                observer.unsubscribe();
            }, () => {
                observer.next(new EFProduct(this.environmentService, this.localizationService, this._checkMapProductType(resource), this._checkMapProductData({ error: true }, resource), resource))
                observer.unsubscribe();
            })
        })
    }

    mapResourceItineraryContent(resource:EFItineraryContentResource, data:any):EFItineraryContent {
        let _retVal = new EFItineraryContent()

        _retVal.resource = resource
        _retVal.element = data.id

        if(data.error) {
            _retVal.error = true;
        }
        else {
            let _product = this._checkMapProductData(data, this.normalizeResourceName(resource));

            _retVal.geolocation = _product.geolocation

            for(let _prop of [
                { from: 'title', to: 'title' }, 
                { from: 'description', to: 'text' }
            ]) {

                let _translations:any = { en_EN: _product[_prop.from] }

                if(_product.translations != null) {
                    for(let _translation of _product.translations) {
                        if(_translation[_prop.from] != null) {
                            _translations[_translation.language] = _translation[_prop.from]
                        }
                    }
                }

                _retVal[_prop.to] = JSON.stringify(_translations)
            }

            if(_product.cover != null && _product.cover != '') _retVal.image = _product.cover;
            else if(_product.images != null && _product.images != '') {
                let _imageList = PGUtilities.tryParseJSON(_product.images)
                if(_imageList != null && _imageList[0] != null) {
                    _retVal.image = _imageList[0]
                }
            }
        }

        return _retVal;
    }

    getResourceItineraryContent(resource:EFItineraryContentResource, element:string, useDataPath?:boolean) {
        return new Observable<EFItineraryContent>((observer) => {
            let _resourceId = this.normalizeResourceName(resource);

            this.http.get(this.environmentService.environment.APIUrl + '/' + (useDataPath ? 'data' : 'public') + '/' + _resourceId + '/' + element, {
                headers: new HttpHeaders({ 'X-Interceptor-Silent': '*' })
            }).subscribe((data) => {
                observer.next(this.mapResourceItineraryContent(resource, data))
                observer.unsubscribe();
            }, () => {
                observer.next(this.mapResourceItineraryContent(resource, { id: element, error: true }))
                observer.unsubscribe();
            })
        })
    }

    getResourceSupplier(resource:string, element:string, useDataPath?:boolean) {
        return new Observable<EFSupplier>((observer) => {
            resource = this.normalizeResourceName(resource);

            let _resourceId = resource.split('~')[0];

            this.http.get(this.environmentService.environment.APIUrl + '/' + (useDataPath ? 'data' : 'public') + '/' + _resourceId + '/' + element, {
                headers: new HttpHeaders({ 'X-Interceptor-Silent': '*' })
            }).subscribe((data) => {
                observer.next(new EFSupplier(data, resource))
                observer.unsubscribe();
            }, () => {
                observer.next(new EFSupplier({ error: true }, resource))
                observer.unsubscribe();
            })
        })
    }

    private _reqResources = ['experience','experience~Event','experience~Package','host','eatery','experienceSupplier','poi','itinerary','article'];

    isExperienceFinderResource(resource:string) {
        return this._reqResources.indexOf(this.normalizeResourceName(resource)) != -1;
    }

    isResourceProduct(product:EFProduct) {
        return this._reqResources.indexOf(product.resource) != -1;
    }

    private _mapMusementExperienceList(type:EFProductType, data:{ [id:string]:any }) {
        let _products:Array<EFProduct> = [];
    
        for(let i in data) {
            _products.push(new EFProduct(this.environmentService, this.localizationService, type, this._checkMapProductData(data[i], 'musement:experience'), 'musement:experience', { origin: 'musement', resource: 'activities', id: i }))
        }

        return _products;
    }

    getMusementResource(type:EFProductType) {
        return new Observable<Array<EFProduct>>((observer) => {
            this._getData(this.environmentService.environment.APIUrl + '/public/musement/activities').subscribe((data) => {
                let _dataById:{ [id:string]:any } = {}

                let _reqNum = 0;

                if(data != null) {
                    for(let _cData of data as Array<any>) {
                        if(_cData.languages == null) _cData.languages = ['en_EN']

                        for(let i = 0; i < _cData.languages.length; i++) {
                            _cData.languages[i] = this.localizationService.normalizeLanguage(_cData.languages[i])
                        }

                        _cData.translations = []

                        _cData.translations.push({
                            language: 'en_EN',
                            title: _cData.title,
                            description: _cData.description,
                            whats_included: _cData.whats_included,
                            useful_information: _cData.useful_information,
                        })

                        if(_cData.category != null && typeof _cData.category == 'object') {
                            _cData.category.translations = []
                        }

                        _dataById[_cData.id] = _cData
                    }

                    for(let _language of this.localizationService.availableApplicationLanguages) {
                        if(_language != 'en_EN') {
                            _reqNum++;

                            this._getData(this.environmentService.environment.APIUrl + '/public/musement/activities?lang=' + _language.replace('_', '-')).subscribe((data) => {
                                if(data != null) {
                                    for(let _cData of data as Array<any>) {
                                        let _product = _dataById[_cData.id];

                                        if(_product != null) {
                                            _product.translations.push({
                                                language: _language,
                                                title: _cData.title,
                                                description: _cData.description,
                                                whats_included: _cData.whats_included,
                                                useful_information: _cData.useful_information,
                                            })

                                            if(_cData.category != null && typeof _cData.category == 'object' && _product.category != null) {
                                                _product.category.translations.push({
                                                    language: _language,
                                                    name: _cData.category.name
                                                })
                                            }
                                        }
                                    }
                                }

                                _reqNum--;
                                if(_reqNum == 0) {
                                    observer.next(this._mapMusementExperienceList(type, _dataById))
                                    observer.complete();
                                }
                            }, () => {
                                _reqNum--;
                                if(_reqNum == 0) {
                                    observer.next(this._mapMusementExperienceList(type, _dataById))
                                    observer.complete();
                                }
                            })
                        }
                    }
                }

                if(_reqNum == 0) {    
                    observer.next(this._mapMusementExperienceList(type, _dataById))
                    observer.complete();
                }
            })
        })
    }

    listProducts(language?:string, filteredTypes?:Array<string>, filteredGroups?:Array<string>, realm?:string) {
        return new Observable<{ [type:string]: Array<EFProduct> }>((observer) => {
            if(language == null) language = this.localizationService.currentLanguage;

            let _reqByType:{ [type:string]: Array<Observable<Array<EFProduct>>> } = {}

            for(let _resource of this._reqResources) {
                let _type = this._checkMapProductType(_resource);

                if(_reqByType[_type] == null) _reqByType[_type] = [];

                _reqByType[_type].push(this.listResourceProducts(_resource, language, filteredGroups, null, realm))
            }

            let _envResources = this.environmentService.environment.ExperienceFinderResources;

            if(realm == null && this.environmentService.publicRealm == null && (filteredGroups == null || filteredGroups.length == 0)) { // solo i prodotti dalle resource hanno un realm o un group, se ci sono dei filtri per group escludo il resto
                if(_envResources != null) {    
                    if(_envResources.indexOf('musement:experience') != -1) {
                        let _type = this._checkMapProductType('musement:experience');
        
                        if(_reqByType[_type] == null) _reqByType[_type] = [];
        
                        _reqByType[_type].push(this.getMusementResource(_type))    
                    }
                }
    
                if(this.environmentService.environment.ExperienceFinderGeoJSON != null) {
    
                    for(let i in this.environmentService.environment.ExperienceFinderGeoJSON) {
                        let _type = 'utility'
    
                        if(_reqByType[_type] == null) _reqByType[_type] = [];
    
                        _reqByType[_type].push(this._getData(this.environmentService.environment.ExperienceFinderGeoJSON[i]).pipe(map((data:EFGeoJSON) => {
                            let _products:Array<EFProduct> = [];
    
                            if(data != null) {
                                for(let j = 0; j < data.features.length; j++) {
                                    let _cFeature = data.features[j];
                                    
                                    let _cLanguages:Array<string> = [];
                                    let _cTranslations:Array<any> = [];
                
                                    let _title:string = null;
                                    let _description:string = null;
                
                                    if(i == 'parking') {
                                        _title = _cFeature.properties.name;
                                        _description = '';
                
                                        if(_cFeature.properties.Tipo_d_uso != null && _cFeature.properties.Tipo_d_uso != '') {
                                            _description += _cFeature.properties.Tipo_d_uso;
                                        }
                
                                        if(_cFeature.properties.parking_spaces != null && _cFeature.properties.parking_spaces != 0) {
                                            if(_description != '') _description += ' - ';
                                            _description += 'Spaces: ' + _cFeature.properties.parking_spaces
                                        }
                                    }
                                    else if(i == 'charging-station') {
                                        _title = 'Charging station';
                                        _description = 'KW: ' + _cFeature.properties.kW + '<br/>Outlets: ' + _cFeature.properties.number_of_outlets;
                                    }
                
                                    for(let _cLanguage of this.localizationService.availableApplicationLanguages) {
                                        _cLanguages.push(_cLanguage);
                
                                        _cTranslations.push({
                                            language: _cLanguage,
                                            title: _title,
                                            description: _description
                                        })                                    
                                    }
                
                                    let _swap = _cFeature.geometry.coordinates[0];
                                    _cFeature.geometry.coordinates[0] = _cFeature.geometry.coordinates[1]
                                    _cFeature.geometry.coordinates[1] = _swap;
                
                                    _products.push(new EFProduct(this.environmentService, this.localizationService, 'utility',  {
                                        id: 'utility_' + i + '_' + j,
                                        category: i,
                                        geolocation: _cFeature.geometry,
                                        address: _cFeature.properties.address,
                                        description: _description,
                                        languages: _cLanguages,
                                        translations: _cTranslations
                                    }, 'utility'))   
                                }
                            }
                
                            return _products;
                        })))
                    }
                }
            }

            let _retVal:{ [type:string]: Array<EFProduct> } = {}

            let _reqCount = 0;

            for(let i in _reqByType) {
                if((filteredTypes == null || filteredTypes.indexOf(i) != -1) && (_reqByType[i] != null && _reqByType[i].length > 0)) {
                    _retVal[i] = [];

                    for(let _observable of _reqByType[i]) {
                        _reqCount++;
                        _observable.subscribe((data) => {
                            _retVal[i] = data;
                            
                            observer.next(_retVal)
                        }, () => {
                        }, () => {
                            _reqCount--
                            if(_reqCount == 0) {
                                observer.complete()
                                observer.unsubscribe()
                            }
                        })
                    }
                }
            }

            if(_reqCount == 0) {
                observer.complete()
                observer.unsubscribe()
            }
        })
    }

    private _getData(url:string) {
        return new Observable<any>((observer) => {
            this.http.get(url).subscribe((data:any) => {
                observer.next(data);
            }, () => {
                observer.next(null);
            }, () => {
                observer.complete();
                observer.unsubscribe();
            })
        })
    }

    getProductDetail(id:string) {
        return new Observable<EFProduct>((observer) => {
            /*if((product.resource == 'municipium:poi' || product.resource == 'municipium:event') && product.base_api != null) {
                let _apiResource = null;
                if(product.resource == 'municipium:poi') _apiResource = 'point_of_interests'
                else if(product.resource == 'municipium:event') _apiResource = 'events'

                let _apiId = product.id.replace(product.resource + '_', '')

                this.http.get(product.base_api + '/' + _apiResource + '/' + _apiId).subscribe((data:any) => {
                    data.base_api = product.base_api;
                    data.dates = product.dates;
                    observer.next(new EFProduct(this.environmentService, this.localizationService, this._checkMapProductType(product.resource), this._checkMapProductData(data, product.resource), product.resource, product.analytics));
                    observer.unsubscribe();
                }, (error) => {
                    observer.next(product);
                    observer.unsubscribe();
                })
            }
            else */
            
            let _resource = id.split('_')[0];
            let _element = id.split('_')[1];

            if(this._reqResources.indexOf(_resource) != -1 || _resource == 'good') {
                this.http.get(this.environmentService.environment.APIUrl + '/public/' + this.normalizeResourceName(_resource.replace(/~.*$/, '')) + '/' +  _element).subscribe((data:any) => {
                    observer.next(new EFProduct(this.environmentService, this.localizationService, this._checkMapProductType(_resource), this._checkMapProductData(data, _resource), _resource, this._getResourceProductAnalytics(_resource, data)))
                    observer.unsubscribe();
                }, (error) => {
                    observer.next(null);
                    observer.unsubscribe();
                })
            }
        })
    }

    getItinerary(id:string, useDataPath?:boolean) {
        return new Observable<EFItinerary>((observer) => {
            this.http.get(this.environmentService.environment.APIUrl + '/' + (useDataPath ? 'data' : 'public') + '/itinerary/' + id, {
                headers: new HttpHeaders({ 'X-Interceptor-Silent': '*' })
            }).subscribe((data) => {
                observer.next(new EFItinerary(data, this, useDataPath))
                observer.unsubscribe();
            })
        })
    }

    private _applyProductsFilterDoesProductMatchLanguages(product:EFProduct, languages:{ [lang:string]: boolean }) {
        if(languages == null || Object.keys(languages).length == 0) {
            return true;
        }
        else {
            if(product.languages) {
                for(let i in languages) {
                    if(languages[i] && product.languages.indexOf(i) != -1) {
                        return true;
                    }
                }
            }
            
            return false;
        }
    }

    private _applyProductsFilterDoesProductMatchGroups(product:EFProduct, groups:Array<string>) {  
        if(groups == null) {
            return true;
        }
        else {
            if(product.group_id == null) {
                return false;
            }
            else {
                for(let _group of groups) {
                    if(product.group_id == _group) {
                        return true;
                    }
                }

                return false;
            }
        }
    }

    private _applyProductsFilterDoesProductMatchTags(product:EFProduct, tags:Array<string>) {  
        if(tags == null) {
            return true;
        }
        else {
            for(let _tag of tags) {
                if(product.tags == null || product.tags.indexOf(_tag.toLowerCase()) == -1) {
                    return false;
                }
            }

            return true;
        }
    }

    private _applyProductsFilterDoesProductMatchInterval(product:EFProduct, interval:{ from: string, to: string }) {
        if(interval == null) return true;
        else {
            let _cDate:Date = new Date(interval.from);
            let _cLastDate:Date = new Date(interval.to);
    
            if(product.dates != null && product.dates.length > 0) {
                for(let _productDate of product.dates) {
                    let _checkDates:Array<Date> = [];

                    if(typeof _productDate == 'string') {
                        _checkDates.push(new Date(_productDate))
                    }
                    else {
                        _checkDates.push(new Date(_productDate.begin))
                        _checkDates.push(new Date(_productDate.end))
                    }
    
                    for(let _date of _checkDates) {
                        if(_date.getTime() > _cDate.getTime() && _date.getTime() < _cLastDate.getTime()) {
                            return true;
                        }
                    }
                }
            }
            else if(product.availability != null) {
                while(_cDate <= _cLastDate) {
                    if(product.availability.isAvailableOn(_cDate.getFullYear(), _cDate.getMonth() + 1, _cDate.getDate())) {
                        return true
                    }
    
                    _cDate.setDate(_cDate.getDate() + 1);
                }
            }
        }
    }

    private _applyProductsFilterDoesProductMatchStars(product:EFProduct, stars:{ [stars:number]: boolean }) {
        if(stars == null) return true;
        else {
            return stars[product.stars] == true;
        }
    }

    private _applyProductsFilterDoesProductMatchCost(product:EFProduct, cost:Array<{ from: number, to: number, selected: boolean }>) {
        if(cost == null) return true;
        else {
            for(let _range of cost) {
                if(_range.selected) {
                    if(product.cost_list != null) {
                        for(let _item of product.cost_list) {
                            if(_range.from <= _item && _range.to >= _item) return true;
                        }
                    }
                }
            }
        }
    }

    private _applyProductsFilterDoesProductMatchUsers(product:EFProduct, users:Array<string>) {
        if(users == null || users.length == 0) return true;
        else return users.indexOf(product.user_id) != -1;
    }

    private _applyProductsFilterDoesProductMatchIds(product:EFProduct, ids:Array<string>) {
        if(ids == null || ids.length == 0) return true;
        else return ids.indexOf(product.id) != -1;
    }

    applyProductsFilter(productsData:Array<EFProduct>, filterData:EFFilterData, presetCategories?:{ [type:string]: boolean }) {
        let _result:Array<EFProduct> = [];

        for(let _product of productsData) {
            if(_product.geolocation != null) {
                if(filterData == null) {
                    _result.push(_product)
                }
                else if(filterData.type == null || filterData.type == _product.type) {
                    let _isIn = true;
    
                    if(filterData.category != null) {
                        // se ci sono delle categorie presettate dall'esterno devo visualizzare sempre solo i prodotti con la categoria settata
                        if((presetCategories != null && presetCategories[_product.type]) || !(filterData.allCategories && filterData.allSubcategories)) {
                            if(_product.category != null) {
                                let _hasCategorySelected = false;
                                let _hasSubcategorySelected = false;
    
                                for(let _category of _product.category.split(',')) {
                                    if(filterData.allCategories || filterData.category[filterData.type][_category]) {
                                        _hasCategorySelected = true;
    
                                        if(filterData.allSubcategories) {
                                            _hasSubcategorySelected = true;
                                        }
                                        else {
                                            if(filterData.subcategory[filterData.type][_category] != null) {
                                                if(_product.subcategory != null) {
                                                    for(let _subcategory of _product.subcategory.split(',')) {
                                                        if(filterData.subcategory[filterData.type][_category][_subcategory]) {
                                                            _hasSubcategorySelected = true;
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
    
                                _isIn = _isIn && _hasCategorySelected && _hasSubcategorySelected;
                            }
                        }
                    }
    
                    let _languageFilter = filterData.language[filterData.type]
                    if(_languageFilter == null && filterData.language['*'] != null) _languageFilter = filterData.language['*'];
    
                    _isIn = _isIn && this._applyProductsFilterDoesProductMatchLanguages(_product, _languageFilter);
    
                    if(filterData.stars[filterData.type] != null && !filterData.allStars) _isIn = _isIn && this._applyProductsFilterDoesProductMatchStars(_product, filterData.stars[filterData.type]);
                    if(filterData.cost[filterData.type] != null && !filterData.allCosts) _isIn = _isIn && this._applyProductsFilterDoesProductMatchCost(_product, filterData.cost[filterData.type]);
                    
                    _isIn = _isIn && this._applyProductsFilterDoesProductMatchGroups(_product, filterData.groups);
                    _isIn = _isIn && this._applyProductsFilterDoesProductMatchTags(_product, filterData.tags);
                    _isIn = _isIn && this._applyProductsFilterDoesProductMatchInterval(_product, filterData.interval);
                    _isIn = _isIn && this._applyProductsFilterDoesProductMatchUsers(_product, filterData.users);
                    _isIn = _isIn && this._applyProductsFilterDoesProductMatchIds(_product, filterData.ids);
                    
                    let _past = filterData.past[filterData.type]
                    
                    if(_past == false) {
                        _isIn = _isIn && !_product.isPastProduct()
                    }
        
                    if(_isIn) {
                        _result.push(_product)
                    }
                }
            }
        } 

        return _result;
    }
    
    applyTextSearch(productsData:Array<EFProduct>, search:string) {
        let _result:Array<EFProduct> = []

        let _searchRegExp:RegExp = null;

        if(search != null) {
            _searchRegExp = new RegExp('(' + PGUtilities.escapeRegExp(search) + ')', 'i');
        }

        for(let _product of productsData) {
            if(_searchRegExp == null) {
                _result.push(_product)
            }
            else {
                for(let _prop of ['title','tags']) {
                    let _checkList = _product.getTranslatedProductField(_prop)

                    if(_checkList != null)  {
                        if(!Array.isArray(_checkList)) {
                            _checkList = [_checkList]
                        }    

                        for(let _check of _checkList) {
                            if(_check != null) {
                                if(typeof _check != 'string') _check = _check.toString()

                                if(_searchRegExp.test(_check)) {
                                    _result.push(_product);
                                    break;
                                }
                            }
                        }
                    }
                }
            }
        }

        return _result;
    }

    getFinderEnvironment(pid:string) {
        return new Observable<EFFinderEnvironment>((observer) => {
            this.http.get(this.environmentService.environment.APIUrl + '/public/finderEnvironment?filter=' + 
                encodeURIComponent(JSON.stringify([{ field: 'pid', operator: '==', value: [pid]}]))).subscribe((data:Array<any>) => {
                observer.next(new EFFinderEnvironment(data[0]))
                observer.unsubscribe()
            }, () => {
                observer.next(null)      
                observer.unsubscribe()
            })
        })
    }
}